import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { BookingManage } from '@/client/BookingManage';
import { BookingPage } from '@/client/BookingPage';
import { PollPage } from '@/client/PollPage';
import { Invite } from '@/client/Invite';
import { MyAccount } from '@/client/MyAccount';
import { MyBookings } from '@/client/MyBookings';
import { ClientSettings } from '@/client/Settings';
import { ClientSignIn } from '@/client/SignIn';
import { CLIENT_ROUTES } from '@/client/routes';
import { RequireSession } from '@/shared/auth';
import { useBrand } from '@/shared/brand/useBrand';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { Icon } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import { NotFound } from './NotFound';
import { LegalPage, CancellationPage, isLegalPath } from '@/shared/legal';

/**
 * procalendar.app — the end client's shell.
 * Mobile-first: a bottom tab bar on phones, a top nav from `sm` up. Most
 * clients arrive from a link on their phone and never see a desktop.
 */
export function ClientShell() {
  const { t } = useI18n();
  const brand = useBrand();
  const location = useLocation();
  const publicLegal = isLegalPath(location.pathname);

  return (
    <div className="flex min-h-dvh flex-col bg-surface text-fg">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-full focus:bg-accent focus:px-3 focus:py-2 focus:text-accent-fg"
      >
        {t('common.skipToContent')}
      </a>

      {!publicLegal && <header className="sticky top-0 z-40 border-b border-border bg-surface-2">
        <div
          className="mx-auto flex h-header max-w-3xl items-center gap-3 px-3 sm:px-6"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <NavLink
            to="/book"
            className="tap-target flex shrink-0 items-center rounded-full"
            aria-label={brand.name}
          >
            <BrandLogo className="h-7 w-auto sm:h-8" />
          </NavLink>

          <nav
            aria-label={t('nav.primary')}
            className="ml-auto hidden items-center gap-1 sm:flex"
          >
            {CLIENT_ROUTES.map((r) => (
              <NavLink key={r.id} to={`/${r.path}`} className={topLink}>
                {t(r.navKey)}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>}

      <main
        id="main"
        className="mx-auto w-full max-w-3xl flex-1 px-3 py-6 sm:px-6 sm:py-8"
      >
        <Routes>
          <Route index element={<Navigate to="/book" replace />} />
          <Route path="privacy" element={<LegalPage document="privacy" />} />
          <Route path="terms" element={<LegalPage document="terms" />} />
          <Route path="baja" element={<CancellationPage />} />
          <Route path="book" element={<BookingPage />} />
          {/* Only `me/*` is gated. Booking stays open to anonymous visitors on
              purpose: the account is created at the end of the flow, never as a
              gate in front of it (docs/ARCHITECTURE.md §3). */}
          <Route
            path="me/bookings"
            element={
              <RequireSession>
                <MyBookings />
              </RequireSession>
            }
          />
          <Route
            path="me/account"
            element={
              <RequireSession>
                <MyAccount />
              </RequireSession>
            }
          />
          <Route path="settings" element={<ClientSettings />} />
          {/* Not a tab: the booking flow sends clients here at the end. */}
          <Route path="signin" element={<ClientSignIn />} />
          <Route path="invite/:token" element={<Invite />} />
          <Route path="poll/:pollId" element={<PollPage />} />
          <Route path="poll" element={<PollPage />} />
          {/* The emailed manage link: procalendar.app/bookings/manage/:token?id= */}
          <Route path="bookings/manage/:token" element={<BookingManage />} />
          {/* The professional's public calendar: procalendar.app/:slug */}
          <Route path=":slug/:eventSlug" element={<BookingPage />} />
          <Route path=":slug" element={<BookingPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {/* Bottom tab bar — phones only. Four 44px targets, safe-area aware. */}
      {!publicLegal && <nav
        aria-label={t('nav.primary')}
        className="sticky bottom-0 z-30 border-t border-border bg-surface-2 sm:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <ul className="mx-auto flex max-w-3xl">
          {CLIENT_ROUTES.map((r) => (
            <li key={r.id} className="flex-1">
              <NavLink to={`/${r.path}`} className={tabLink}>
                <Icon name={r.icon} />
                <span className="truncate text-[0.6875rem] leading-tight">
                  {t(r.navKey)}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>}
    </div>
  );
}

function topLink({ isActive }: { isActive: boolean }): string {
  return [
    'rounded-token px-3 py-2 text-sm transition-colors',
    isActive
      ? 'bg-accent-soft font-medium text-accent'
      : 'text-muted hover:bg-surface-3 hover:text-fg',
  ].join(' ');
}

function tabLink({ isActive }: { isActive: boolean }): string {
  return [
    'tap-target flex flex-col items-center justify-center gap-1 px-1 py-2 transition-colors',
    isActive ? 'text-accent' : 'text-muted',
  ].join(' ');
}
